import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FileText, ExternalLink, Shield } from 'lucide-react';
import './Blogs.css';

const Blogs = () => {
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await fetch('/api/blogs');
      const data = await response.json();
      setBlogs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBlogClick = (blogId) => {
    navigate(`/blogs/${blogId}`);
  };

  const products = ['All', 'WhiteMatter', 'SafePulse', 'TrustMark'];
  const categories = ['All', ...new Set((Array.isArray(blogs) ? blogs : []).map(blog => blog.Category))];

  const filteredBlogs = (Array.isArray(blogs) ? blogs : []).filter(blog => {
    const matchesProduct = selectedProduct === 'All' || blog.Product === selectedProduct;
    const matchesCategory = selectedCategory === 'All' || blog.Category === selectedCategory;
    return matchesProduct && matchesCategory;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="blogs-page">
      <section className="blogs-hero tech-bg">
        <div className="blogs-hero-content">
          <motion.h1
            className="blogs-hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Blogs
          </motion.h1>
          <motion.p
            className="blogs-hero-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Insights, updates, and stories from <span className="blogs-gradient-text">Erasys</span>
          </motion.p>
        </div>
      </section>

      <section className="blogs-grid-section">
        <div className="blogs-container">
          <div className="blogs-filters">
            <div className="product-filter">
              <label htmlFor="product-select" className="filter-label">Product</label>
              <select
                id="product-select"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                className="product-select"
              >
                {products.map((product) => (
                  <option key={product} value={product}>
                    {product}
                  </option>
                ))}
              </select>
            </div>
            <div className="category-filters">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`category-filter-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="results-count">
            Showing {filteredBlogs.length} of {blogs.length} blogs
          </div>

          <motion.div
            className="blogs-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {loading ? (
              <div className="loading-state">Loading blogs...</div>
            ) : filteredBlogs.length === 0 ? (
              <div className="empty-state">No blogs found</div>
            ) : (
              filteredBlogs.map((blog) => {
                return (
                  <motion.div
                    key={blog.BlogID}
                    className="blog-card"
                    variants={itemVariants}
                    whileHover={{ 
                      y: -8,
                      transition: { duration: 0.3 }
                    }}
                    onClick={() => handleBlogClick(blog.BlogID)}
                  >
                    <div className="blog-card-inner">
                      {blog.CardImageFileName ? (
                        <div className="blog-image-wrapper">
                          <img src={`/uploads/${blog.CardImageFileName}`} alt={blog.Title} className="blog-image" />
                        </div>
                      ) : (
                        <div className="blog-icon-wrapper">
                          <div 
                            className="blog-icon"
                            style={{ backgroundColor: `${blog.Color}15` }}
                          >
                            <Shield 
                              size={32} 
                              style={{ color: blog.Color }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="blog-content">
                        <span 
                          className="blog-category"
                          style={{ color: blog.Color }}
                        >
                          {blog.Category}
                        </span>
                        <h3 className="blog-card-title">
                          {blog.Title}
                        </h3>
                        <p className="blog-author">
                          By {blog.Author}
                        </p>
                        <p className="blog-description">
                          {blog.Description}
                        </p>
                      </div>

                      <div className="blog-footer">
                        <div className="blog-cta">
                          <FileText size={18} />
                          <span>Read Article</span>
                          <ExternalLink size={16} className="external-icon" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Blogs;
